import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Compact CSE market open/closed pill for the top bar.
 * Polls /api/market/status every 60s for state changes and *also* updates
 * the on-screen countdown text every 30s by computing it client-side from
 * the ISO `open_at` / `close_at` returned by the backend. This way the user
 * sees the timer tick down without waiting for a server poll.
 */
const formatCountdown = (ms) => {
  if (ms == null || isNaN(ms) || ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours <= 0) return `${mins}m`;
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

const MarketStatusPill = () => {
  const [status, setStatus] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiGet("/market/status")
        .then((s) => { if (!cancelled) setStatus(s); })
        .catch(() => {});
    };
    load();
    const poll = setInterval(load, 60_000);
    // Tick the on-screen clock every 30s so the countdown is always fresh.
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => { cancelled = true; clearInterval(poll); clearInterval(tick); };
  }, []);

  if (!status) return null;
  const isOpen = !!status.is_open;
  const dotCls = isOpen
    ? "bg-[hsl(var(--success))]"
    : "bg-[hsl(var(--destructive))]";
  const tone = isOpen
    ? "border-[hsl(var(--success))]/30 text-[hsl(var(--success))]"
    : "border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))]";

  // Live-computed countdown from the most recent server snapshot
  const targetIso = isOpen ? status.close_at : status.open_at;
  const targetMs = targetIso ? new Date(targetIso).getTime() : null;
  const liveCountdown = targetMs != null
    ? formatCountdown(targetMs - now)
    : status.countdown_human;

  let primary = "";
  if (isOpen) primary = `Closes in ${liveCountdown}`;
  else if (status.state === "closed_weekend") primary = "Weekend";
  else if (status.state === "closed_holiday") primary = "Holiday";
  else if (status.state === "closed_after_hours") primary = "After hours";
  else if (status.state === "closed_before_open") primary = `Opens in ${liveCountdown}`;
  else primary = "Closed";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-1.5 rounded-full border ${tone} px-2.5 py-1 text-[11px] font-medium cursor-help`}
            data-testid="market-status-pill"
            role="status"
            aria-label={`CSE market ${status.label}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
            <span className="hidden sm:inline">Market {status.label}</span>
            <span className="sm:hidden">{status.label}</span>
            <span className="hidden md:inline text-muted-foreground font-normal">· {primary}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[280px]">
          <div className="space-y-1 text-xs">
            <div className="font-semibold">CSE Market — {status.label}</div>
            <div>{status.reason}</div>
            {status.today_holiday && (
              <div className="text-muted-foreground">{status.today_holiday.name}</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default MarketStatusPill;
